import { config, useSpring } from '@react-spring/web'
import { extendConsole } from '@shared/logging/console'
import type {
    AnimationConfig,
    AnimationConfigWithPath,
    AnimationItem,
    LottiePlayer,
} from 'lottie-web'
import { createRef, useEffect, useRef, useState } from 'react'
import useMeasure from 'react-use-measure'
import ResizeObserver from 'resize-observer-polyfill'
import type { LiteralUnion } from 'type-fest'

export type AnimationName = LiteralUnion<'stepButtonPress', string>

type Animations = { [key in AnimationName]: any }

export type AnimationStatus =
    | 'loading'
    | 'ready'
    | 'playing'
    | 'complete'
    | 'error'

const logger = extendConsole('animation')

type Lottie = { player: LottiePlayer; animations: Animations }

const loadLottie = async (): Promise<Lottie | null> => {
    try {
        const [{ default: player }, stepButtonPress] = await Promise.all([
            import('lottie-web'),
            import('../../assets/animations/button-press.json'),
        ])
        return {
            player,
            animations: {
                stepButtonPress,
            },
        }
    } catch (e) {
        logger.warn(e, 'Failed to load animation player')
        return null
    }
}

async function loadAnimation(
    animationData: any,
    options: AnimationConfig<'svg'>,
) {
    const lottie = await loadLottie()

    if (!lottie) return null

    return lottie.player.loadAnimation({
        animationData,
        ...options,
    })
}

async function loadAnimationByName(
    name: AnimationName,
    options: AnimationConfig<'svg'>,
) {
    const lottie = await loadLottie()

    if (!lottie) return null

    // Clone the animation data to avoid "object is not extensible" errors
    const animationData = { ...lottie.animations[name] }

    return lottie.player.loadAnimation({
        animationData,
        ...options,
    })
}

/** Hook to load a finite animation into the container.
 * Starts when you call play().
 * JSON for this animation is loaded lazily. Exposes animation status for easy styling.
 * Powered by Lottie. */
export function useFiniteAnimation(
    name: AnimationName,
    { resetTimeout = 100 } = {},
) {
    const container = createRef<HTMLDivElement>()
    const animation = useRef<AnimationItem | null>(null)
    const [status, setStatus] = useState<AnimationStatus>('loading')

    useEffect(() => {
        if (status === 'complete') {
            setTimeout(() => setStatus('ready'), resetTimeout)
        }
    }, [status])

    useEffect(() => {
        if (!container.current) return

        loadAnimationByName(name, {
            autoplay: false,
            loop: false,
            container: container.current,
        }).then((a) => {
            if (!a) return
            animation.current = a
            a.addEventListener('complete', () => setStatus('complete'))
        })

        setStatus('ready')

        return () => {
            if (!animation?.current || !container.current) return
            animation.current.destroy()
        }
    }, [])

    const play = () => {
        if (!animation?.current) return
        setStatus('playing')
        animation.current.goToAndPlay(0)
    }

    return [container, play, status] as const
}

/** Hook to perform pretty dropdown animation.
 * Attach ref to the content node, that won't change sise.
 * Wrap that element into an animated.div. Pass wrapper style to that div. */
export const useDropdownAnimation = (open: boolean) => {
    const [contentRef, { height }] = useMeasure({ polyfill: ResizeObserver })

    const wrapperStyle = useSpring({
        pointerEvents: open ? 'all' : 'none',
        opacity: open ? 1 : 0,
        height: open ? height : 0,
        overflow: 'hidden',
        config: config.stiff,
    })

    return [contentRef as any, wrapperStyle]
}

/** Hook to load custom json animation into the container. Powered by Lottie. */
export const useJSONAnimation = <E extends HTMLElement>({
    path,
    ...restOfOptions
}: Partial<
    Omit<AnimationConfigWithPath, 'container'>
>): React.RefObject<E | null> => {
    const animation = useRef<AnimationItem | null>(null)
    const container = createRef<E>()

    useEffect(() => {
        if (!path || !container.current) return

        if (animation.current) animation.current.destroy()

        loadAnimation(path, {
            container: container.current,
            ...restOfOptions,
        }).then((a) => (animation.current = a))
    }, [path])

    return container
}
type UseLoaderAnimationOptions = {
    enabled?: boolean // Controls when to show/hide the loader
}

export const useLoaderAnimation = ({
    enabled = true,
}: UseLoaderAnimationOptions = {}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const animationRef = useRef<AnimationItem | null>(null)

    useEffect(() => {
        const loadLoaderAnimation = async () => {
            if (!containerRef.current || !enabled) return

            try {
                // Load lottie player and loader animation
                const [{ default: player }, loaderData] = await Promise.all([
                    import('lottie-web'),
                    import('../../assets/animations/loader.json'),
                ])

                // Clean up existing animation
                if (animationRef.current) {
                    animationRef.current.destroy()
                    animationRef.current = null
                }

                // Load the loader animation
                animationRef.current = player.loadAnimation({
                    container: containerRef.current,
                    renderer: 'svg',
                    loop: true,
                    autoplay: true,
                    animationData: loaderData.default || loaderData,
                })
            } catch (error) {
                console.warn('Failed to load loader animation:', error)
            }
        }

        if (enabled) {
            loadLoaderAnimation()
        } else {
            // Clean up when disabled
            if (animationRef.current) {
                animationRef.current.destroy()
                animationRef.current = null
            }
        }

        // Cleanup on unmount
        return () => {
            if (animationRef.current) {
                animationRef.current.destroy()
            }
        }
    }, [enabled])

    return containerRef
}
